import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Clock, Eye, EyeOff, Fingerprint, CheckCircle2 } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getRecaptchaToken } from '../utils/recaptcha';
import {
  isNative,
  hasBiometricCredentials,
  biometricSignIn,
  storeBiometricToken,
} from '../native';
import axios from 'axios';
import { landingPath } from '../components/nav/navConfig';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const SignIn = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Detect previously-enrolled biometric credentials on native shell. Web
  // browsers always see `isNative() === false`, so this is a no-op there.
  useEffect(() => {
    (async () => {
      if (isNative()) {
        setBiometricAvailable(await hasBiometricCredentials());
      }
    })();
  }, []);

  const handleBiometricSignIn = async () => {
    setBiometricLoading(true);
    try {
      const creds = await biometricSignIn();
      if (!creds?.username || !creds?.password) {
        throw new Error('No stored credentials');
      }
      // Stored "password" is actually the previous JWT — try to refresh
      // /users/me with it. If that succeeds we're signed in.
      const meRes = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${creds.password}` },
      });
      if (meRes?.data?.user) {
        login(creds.password, meRes.data.user, meRes.data.company);
        toast({ title: 'Welcome back', description: 'Signed in with biometrics.' });
        const r = meRes.data.user.role;
        navigate(r === 'admin' || r === 'supervisor' ? '/dashboard' : '/clock-in');
        return;
      }
      throw new Error('Stored session expired');
    } catch (err) {
      console.warn('Biometric sign-in failed:', err);
      toast({
        title: 'Biometric sign-in failed',
        description: 'Please sign in with your password — your saved session may have expired.',
        variant: 'destructive',
      });
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Get reCAPTCHA token (supports E2E bypass via localStorage)
      const recaptcha_token = await getRecaptchaToken(executeRecaptcha, 'login');
      const response = await axios.post(`${API}/auth/signin`, { ...formData, recaptcha_token });
      
      // 2FA-enabled accounts get only a short-lived pre-auth token — the real
      // session JWT is issued by /2fa/verify-login (Jun 2026 security fix).
      if (response.data.requires_2fa) {
        navigate('/2fa-verify', {
          state: {
            email: formData.email,
            preAuthToken: response.data.pre_auth_token
          }
        });
        return;
      }
      
      // Normal login flow
      login(response.data.token, response.data.user, response.data.company);

      // On the native shell, offer to save credentials behind Face ID / Touch ID
      // so the next launch can skip the password. Silent no-op on web.
      if (isNative()) {
        try {
          await storeBiometricToken(formData.email, response.data.token);
        } catch (e) {
          // Non-fatal — user can still sign in with password next time.
          console.warn('biometric enroll skipped:', e);
        }
      }
      
      // Check if this is first login and password change is required
      if (response.data.user.first_login) {
        toast({
          title: 'Password Change Required',
          description: 'Please change your temporary password to continue.',
          duration: 5000
        });
        navigate('/change-password-first-login');
      } else if (
        response.data.company?.require_2fa &&
        !response.data.user.two_factor_enabled
      ) {
        // Company-level policy: 2FA is mandatory for everyone in this tenant.
        // Users without it are routed to the setup screen instead of the
        // main app. Once they enable 2FA the redirect won't fire again.
        toast({
          title: 'Two-Factor Setup Required',
          description: 'Your company requires 2FA. Please set it up to continue.',
          duration: 6000,
        });
        navigate('/security-setup?required=2fa');
      } else {
        toast({
          title: 'Success',
          description: 'Signed in successfully!',
        });
        // Role-aware landing (Jun 2026): workers get the one-tap Clock In
        // screen; admins & supervisors land on the Dashboard they came for.
        const r = response.data.user.role;
        const next = new URLSearchParams(window.location.search).get('next');
        if (next && next.startsWith('/') && !next.startsWith('//')) {
          navigate(next);
        } else {
          // Landing-page choice (Sep 2026): Settings → Profile → "After I sign in".
          // Falls back to the old role default if the preference can't be read.
          let landing = null;
          try {
            const prefs = await axios.get(`${API}/me/nav-prefs`, { headers: { Authorization: `Bearer ${response.data.token}` } });
            landing = prefs.data?.landing;
          } catch { /* keep default */ }
          navigate(landing ? landingPath(landing) : (r === 'admin' || r === 'supervisor' ? '/home?tab=company' : '/clock-in'));
        }
      }
    } catch (error) {
      console.error('Sign in error:', error);
      
      let errorMessage = 'Unable to sign in. Please try again.';
      
      // Check if this is a network error (offline or backend unreachable)
      if (!error.response) {
        errorMessage = 'Cannot connect to server. Please check your internet connection and try again.';
      } else if (error.response.status === 401) {
        // Authentication failed
        errorMessage = error.response?.data?.detail || 'Incorrect email or password. Please check your credentials and try again.';
      } else if (error.response.status === 422) {
        // Validation error
        errorMessage = 'Invalid email or password format. Please check and try again.';
      } else {
        // Other errors
        errorMessage = error.response?.data?.detail || error.response?.data?.message || 'An error occurred. Please try again later.';
      }
      
      toast({
        title: 'Sign In Failed',
        description: errorMessage,
        variant: 'destructive',
        duration: 8000
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-2">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src={process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_custom-timetrack/artifacts/sc80sbua_Hines%20Time.png"}
              alt="Hines Time Logo"
              className="h-32 object-contain"
            />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-blue-600">Welcome Back</CardTitle>
            <CardDescription className="text-base mt-2">Sign in to track your time</CardDescription>
          </div>
          {(() => {
            const flags = new URLSearchParams(window.location.search);
            const reactivated = flags.get('reactivated') === '1';
            const alreadyActive = flags.get('already_active') === '1';
            if (!reactivated && !alreadyActive) return null;
            return (
              <div className="p-3 rounded-lg bg-green-50 border border-green-300 flex items-start gap-2 text-left" data-testid="reactivated-banner">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    {reactivated ? "Welcome back — you're reactivated! 🎉" : 'Your subscription is already active.'}
                  </p>
                  <p className="text-xs text-green-700">
                    Your subscription is active and all your data is right where you left it. Sign in to continue.
                  </p>
                </div>
              </div>
            );
          })()}
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSignIn} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold transition-all"
              disabled={isLoading}
              data-testid="signin-submit-btn"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
            <p className="text-[11px] text-center mt-3 text-slate-400">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a> apply.
            </p>
          </form>

          {biometricAvailable && (
            <div className="mt-4">
              <Button
                type="button"
                onClick={handleBiometricSignIn}
                disabled={biometricLoading}
                variant="outline"
                className="w-full h-12 border-2"
                data-testid="signin-biometric-btn"
              >
                <Fingerprint className="w-5 h-5 mr-2" />
                {biometricLoading ? 'Authenticating…' : 'Sign in with Face ID / Touch ID'}
              </Button>
            </div>
          )}

          <div className="mt-6 space-y-3 text-center">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors block w-full"
            >
              Forgot Password?
            </button>
            <div className="pt-3 border-t">
              <p className="text-sm text-slate-400">
                New to Hines Time?{' '}
                <button
                  onClick={() => navigate('/register')}
                  className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                >
                  Register Your Company
                </button>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignIn;